import { Link, Navigate } from 'react-router-dom';
import useFetch, { Status } from '../../../hooks/fetch.hook';
import type { UserApiModel } from '../models/user.api-model';
import Spinner from '../../Shared/Spinner';
import MunicipalityApiModel from '../models/municipality.api-model';
import type { IMunicipality } from '../../../models/municipality.model';
import LinkListWithHeading from '../Shared/LinkListWithHeading';
import PageIntroduction from '../Shared/PageIntroduction';
import Alert from '../../Shared/Alert';
import RequestVerificationTokenInput from '../../Shared/RequestVerificationTokenInput';

function processReceivedMunicipalitiesData(data: MunicipalityApiModel[]): IMunicipality[] {
  return data?.map((d) => MunicipalityApiModel.mapToBusinessModel(d)) ?? [];
}

function MunicipalityAdministrator() {
  // === STATE ===
  const [{ status: getUserStatus, httpResponseStatusCode: getUserHttpResponseStatusCode }] = useFetch<UserApiModel[]>('/api/paegnaben/municipality-administrator/current');
  const [{ status: getMunicipalitiesStatus, data: getMunicipalitiesData, httpResponseStatusCode: getMunicipalitiesHttpResponseStatusCode }] = useFetch<MunicipalityApiModel[]>(
    '/api/paegnaben/municipality-administrator/current/municipalities',
    getUserStatus !== Status.success,
  );

  // === VARIABLES ===
  const loadingDataInProgress = getUserStatus === Status.loading || getMunicipalitiesStatus === Status.loading;
  const municipalities = getMunicipalitiesData ? processReceivedMunicipalitiesData(getMunicipalitiesData) : [];

  // === RENDER ===
  if (getMunicipalitiesHttpResponseStatusCode === 401 || getUserHttpResponseStatusCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }

  if ((getUserStatus === Status.failed && getUserHttpResponseStatusCode === 403) || (getMunicipalitiesStatus === Status.failed && getMunicipalitiesHttpResponseStatusCode === 403)) {
    return <Navigate to="/atkomst-saknas" />;
  }

  return (
    <div className="municipality-administrator">
      {loadingDataInProgress && <Spinner className="my-4 my-md-5" />}
      {!loadingDataInProgress && getUserStatus === Status.success && (
        <>
          <PageIntroduction heading={'Mina sidor'} />
          {getMunicipalitiesStatus === Status.success && (
            <LinkListWithHeading
              heading="Mina kommuner"
              className="mb-4 mb-md-5"
              listItems={municipalities.map((item) => (
                <Link key={item.gid} to={`/kommuner/${item.gid}`} className="text-decoration-none">
                  <span>{item.name}</span>
                </Link>
              ))}
            />
          )}
          {getMunicipalitiesStatus === Status.failed && <Alert show={true} heading="Det gick inte att hämta kommuner" closable={false} />}
          <LinkListWithHeading
            heading="Hantera konto"
            listItems={[
              <Link key="mina-uppgifter" to={'/mina-uppgifter'} className="text-decoration-none">
                <span>Mina uppgifter</span>
              </Link>,
              <form key={'logga-ut'} action="/konto/logga-ut" method="post">
                <RequestVerificationTokenInput />
                <button type="submit" className="btn btn-link text-decoration-none w-100 text-start">
                  <span className="">{'Logga ut'}</span>
                </button>
              </form>,
            ]}
            className="mt-4 mt-sm-5"
          />
        </>
      )}
    </div>
  );
}

export default MunicipalityAdministrator;